import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, coverUri } from "../lib/api";
import { greeting, fmtDateLong, fmtTime, fmtDateShort } from "../lib/format";
import { useAuth } from "../auth";
import { Spinner, EmptyState, ProgressBar, Button } from "../components/ui";
import { Icon } from "../components/icons";
import { useInstallPrompt } from "../lib/pwa";
import { StudySetlistButton } from "../components/StudySetlist";
import { usePlayer, songsQueue } from "../lib/player";
import { can } from "../lib/permissions";
import { HomePlayer } from "../components/HomePlayer";
import { HomeAnnouncements } from "../components/AnnouncementCard";

const REHEARSAL_BG = "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=1000&q=80";

function InstallBanner() {
  const { canPrompt, needsIosHint, install } = useInstallPrompt();
  if (!canPrompt && !needsIosHint) return null;
  return (
    <div className="card" style={{ margin: "0 0 4px", padding: 14, display: "flex", alignItems: "center", gap: 12 }} data-testid="install-banner">
      <div style={{ width: 40, height: 40, borderRadius: 10, overflow: "hidden", flexShrink: 0 }}>
        <img src="/icons/icon-192.png" alt="" style={{ width: "100%", height: "100%" }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "var(--f-medium)", fontSize: 14 }}>Instalar a aplicação</div>
        <div className="t-hint">{needsIosHint ? "No Safari: Partilhar → Adicionar ao ecrã principal." : "Adicione o Urban Lisbon Choir ao seu dispositivo."}</div>
      </div>
      {canPrompt ? <Button className="auto sm" onClick={install} data-testid="install-button">INSTALAR</Button> : null}
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();
  const q = useQuery({ queryKey: ["home"], queryFn: () => api("/home") });

  const first = (q.data?.user?.name || user?.name || "").split(" ")[0];
  const voice = q.data?.user?.voice || user?.voice;
  const reh = q.data?.next_rehearsal;
  const setlists = (q.data?.setlists || []).filter((e) => e.can_study);
  const player = usePlayer();
  // Admin highlights served by the shared backend (same list as the Mobile app).
  const news = q.data?.highlights?.songs || [];
  const featuredSetlists = q.data?.highlights?.setlists || [];

  return (
    <>
      <div className="page-header">
        <span className="t-kicker">URBAN LISBON CHOIR</span>
      </div>
      {q.isLoading ? (
        <Spinner />
      ) : q.isError ? (
        <div className="page" style={{ maxWidth: 1100 }}>
          <EmptyState icon="cloud-offline-outline" text="Não foi possível carregar os dados." testid="home-error" />
          <Button className="auto" onClick={() => q.refetch()} data-testid="home-retry-button">TENTAR NOVAMENTE</Button>
        </div>
      ) : (
        <div className="page ulc-fade" style={{ maxWidth: 1100 }} data-testid="home-scroll">
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 20 }}>
            <h1 className="t-h1" data-testid="home-greeting">
              {greeting()}, {first}.
            </h1>
            {voice ? (
              <span
                data-testid="home-voice-badge"
                style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "var(--brand-tertiary)", color: "var(--on-surface)", borderRadius: 999, padding: "6px 12px", fontFamily: "var(--f-medium)", fontSize: 12, letterSpacing: 2 }}
              >
                <Icon name="mic" size={12} /> {voice.toUpperCase()}
              </span>
            ) : null}
          </div>

          <InstallBanner />

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16 }} data-testid="home-quick-links">
            <Link to="/calendar" className="chip" data-testid="home-quick-calendar"><Icon name="calendar-outline" size={14} /> CALENDÁRIO</Link>
            <Link to="/listas" className="chip" data-testid="home-quick-playlists"><Icon name="list-music" size={14} /> AS MINHAS LISTAS</Link>
            <Link to="/announcements" className="chip" data-testid="home-quick-announcements"><Icon name="megaphone" size={14} /> AVISOS</Link>
            <Link to="/attendance" className="chip" data-testid="home-quick-attendance"><Icon name="stats" size={14} /> ASSIDUIDADE</Link>
          </div>

          <HomeAnnouncements canManage={can(user, "announcements.manage")} />

          <div style={{ marginTop: 20 }}><HomePlayer continueSongs={q.data?.continue || []} highlightSongs={news} user={user} /></div>

          {featuredSetlists.length ? (
            <>
              <div className="t-section" style={{ marginTop: 28, marginBottom: 12 }} data-testid="home-highlights">Destaques · alinhamentos</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="home-featured-setlists">
                {featuredSetlists.map((sl) => (
                  <div key={sl.id} className="card" style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, borderColor: "var(--brand-primary)" }} data-testid={`home-featured-${sl.id}`}>
                    <Link to={`/alinhamentos/${sl.id}`} style={{ flex: 1, minWidth: 0, color: "inherit", textDecoration: "none" }}>
                      <div style={{ color: "var(--brand-primary)", fontFamily: "var(--f-medium)", fontSize: 10, letterSpacing: 1.5 }}>ALINHAMENTO OFICIAL · {sl.songs_count ?? (sl.songs || sl.song_ids || []).length} MÚSICAS</div>
                      <div style={{ fontFamily: "var(--f-medium)", fontSize: 16 }}>{sl.name}</div>
                    </Link>
                    <Button className="auto sm" icon="play" disabled={!(sl.songs_count ?? (sl.song_ids || []).length)} onClick={async () => { const full = sl.songs ? sl : await api(`/setlists/${sl.id}`); player.playQueue(songsQueue(full.songs || []), 0, { key: `setlist:${sl.id}`, label: sl.name, path: `/alinhamentos/${sl.id}` }); }} data-testid={`home-featured-play-${sl.id}`}>ESTUDAR</Button>
                  </div>
                ))}
              </div>
            </>
          ) : null}

          {news.length ? (
            <>
              <div className="row-between" style={{ marginTop: 28, marginBottom: 12 }}>
                <span className="t-section">Destaques · músicas</span>
                <Link to="/repertorio" className="t-hint" style={{ textDecoration: "none" }} data-testid="home-news-all">Ver repertório</Link>
              </div>
              <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8 }} data-testid="home-news">
                {news.map((s) => (
                  <Link key={s.id} to={`/song/${s.id}`} state={{ from: { path: "/", label: "Início" } }} className="card" data-testid={`home-news-${s.id}`}
                    style={{ width: 160, flexShrink: 0, padding: 10, textDecoration: "none", color: "inherit", display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ position: "relative" }}>
                      <img className="cover" src={coverUri(s.cover_url)} alt="" style={{ width: "100%", aspectRatio: "1", borderRadius: 8, objectFit: "cover" }} />
                      <span className="pill doing" style={{ position: "absolute", top: 6, left: 6 }}>DESTAQUE</span>
                    </div>
                    <div style={{ fontFamily: "var(--f-medium)", fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.title}</div>
                    <div className="t-hint" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.artist}</div>
                  </Link>
                ))}
              </div>
            </>
          ) : null}

          <div className="t-section" style={{ marginTop: 28, marginBottom: 12 }}>Continuar a estudar</div>
          {q.data?.continue?.length ? (
            <div style={{ display: "flex", gap: 16, overflowX: "auto", paddingBottom: 8 }}>
              {q.data.continue.map((song) => (
                <Link
                  key={song.id}
                  to={`/song/${song.id}`}
                  data-testid={`continue-card-${song.title.toLowerCase().replace(/\s+/g, "-")}`}
                  style={{ position: "relative", width: 220, height: 280, borderRadius: "var(--r-lg)", overflow: "hidden", background: "var(--surface-2)", flexShrink: 0, textDecoration: "none" }}
                >
                  <img src={coverUri(song.cover_url)} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                  <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, var(--scrim) 10%, transparent 70%)" }} />
                  <div style={{ position: "absolute", top: 12, right: 12, width: 40, height: 40, borderRadius: 999, background: "var(--brand-primary)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon name="play" size={18} color="var(--on-brand)" />
                  </div>
                  <div style={{ position: "absolute", left: 16, right: 16, bottom: 16, display: "flex", flexDirection: "column", gap: 6 }}>
                    <div style={{ color: "var(--on-surface)", fontFamily: "var(--f-medium)", fontSize: 20, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{song.title}</div>
                    <div style={{ color: "var(--on-surface-3)", fontSize: 13 }}>{song.artist}</div>
                    <ProgressBar value={song.progress} />
                    <div style={{ color: "var(--on-surface-3)", fontSize: 12 }}>{song.progress}% estudado</div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState icon="musical-note-outline" text="Nenhuma música em estudo." />
          )}

          <div className="row-between" style={{ marginTop: 28, marginBottom: 12 }}>
            <span className="t-section">Próximo ensaio</span>
            <Link to="/calendar" data-testid="home-calendar-link" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--on-surface-3)", textDecoration: "none", fontFamily: "var(--f-medium)", fontSize: 13 }}>
              <Icon name="calendar-outline" size={14} /> Calendário
            </Link>
          </div>
          {reh ? (
            <Link to={`/events/${reh?.id}`} data-testid="next-rehearsal-card" style={{ display: "block", position: "relative", borderRadius: "var(--r-lg)", overflow: "hidden", minHeight: 160, border: "1px solid var(--border)", textDecoration: "none" }}>
              <img src={REHEARSAL_BG} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", filter: "blur(2px)", opacity: 0.5 }} />
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, var(--scrim-soft), var(--scrim))" }} />
              <div style={{ position: "relative", padding: 24, display: "flex", flexDirection: "column", gap: 8 }}>
                <div data-testid="next-rehearsal-date" style={{ fontFamily: "var(--f-medium)", fontSize: 22 }}>{fmtDateLong(reh.starts_at)}</div>
                <Meta icon="time-outline" testid="next-rehearsal-time">{fmtTime(reh.starts_at)}</Meta>
                {reh.location ? <Meta icon="location-outline" testid="next-rehearsal-location">{[reh.location, reh.address].filter(Boolean).join(" · ")}</Meta> : null}
                {reh.notes ? <div className="t-hint">{reh.notes}</div> : null}
                {reh.songs?.length ? <Meta icon="musical-notes-outline" testid="next-rehearsal-setlist">Alinhamento: {reh.songs.map((x) => x.title).join(" · ")}</Meta> : null}
                {reh.songs?.length ? <div style={{ marginTop: 6 }}><StudySetlistButton event={reh} testid="home-study-next-rehearsal" /></div> : null}
              </div>
            </Link>
          ) : (
            <EmptyState icon="calendar-outline" text="Sem ensaios agendados." />
          )}

          {setlists.length ? (
            <>
              <div className="t-section" style={{ marginTop: 28, marginBottom: 12 }}>Alinhamentos</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {setlists.map((e) => (
                  <Link key={e.id} to={`/events/${e.id}`} data-testid={`home-setlist-${e.id}`} className="card" style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, textDecoration: "none", color: "inherit" }}>
                    <div style={{ width: 40, height: 40, borderRadius: 999, background: "var(--brand-tertiary)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Icon name="list" size={18} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: "var(--muted)", fontFamily: "var(--f-medium)", fontSize: 10, letterSpacing: 1.5 }}>
                        {fmtDateShort(e.starts_at)} · {e.songs.length} {e.songs.length === 1 ? "MÚSICA" : "MÚSICAS"}
                      </div>
                      <div style={{ fontFamily: "var(--f-medium)", fontSize: 15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.title}</div>
                      <div style={{ color: "var(--on-surface-3)", fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.songs.map((x) => x.title).join(" · ")}</div>
                    </div>
                    <StudySetlistButton event={e} testid={`home-study-${e.id}`} className="auto sm ghost" />
                    <Icon name="chevron-forward" size={18} color="var(--muted)" />
                  </Link>
                ))}
              </div>
            </>
          ) : null}
        </div>
      )}
    </>
  );
}

function Meta({ icon, children, testid }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--on-surface-3)", fontSize: 14 }} data-testid={testid}>
      <Icon name={icon} size={16} color="var(--on-surface-3)" />
      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{children}</span>
    </div>
  );
}
